import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import { ROUTES } from 'src/constants';
import LoadingScreen from 'src/components/LoadingScreen';
import { fetchSetupAssistantDefaultScreen } from 'src/views/pages/SetupAssistantView/setupAssistantApi';

/**
 * SetupAssistantDefaultScreen — CON-1698 entry point.
 *
 * The first entry in RedirectLogic's /app destination list. Mounted ONLY
 * when the render has already fallen through every route lock above the
 * list AND the account holds SETUP_ASSISTANT (condition 1, checked by
 * RedirectLogic before this mounts). Mounting is what triggers the server
 * call, so an account that never gets here never makes it.
 *
 * The server answers the remaining three conditions in order — account
 * created on or after the cutoff, verified steps not all complete, day
 * counter still open — and the last of those WRITES: it spends one of the
 * account's seven default-screen days. That is why this component, and not
 * a top-level effect in RedirectLogic, owns the call: RedirectLogic renders
 * on every page and re-renders on every store change, and an effect there
 * could fire for an account that was about to be sent to the Account page
 * instead.
 *
 * This is a DEFAULT, not a redirect. It decides where bare /app lands and
 * nothing else — the DashboardLayout's RedirectLogic passes no destination
 * list, so this never mounts on Order Follow-Up or anywhere a consultant
 * navigated to on purpose.
 *
 * Fails closed: any error, and /app lands where it always did (`fallback`,
 * the first permitted entry of the rest of the list).
 */
function SetupAssistantDefaultScreen({ fallback }) {
  // null = still asking, true/false = answered.
  const [shouldShow, setShouldShow] = useState(null);

  useEffect(() => {
    let cancelled = false;

    fetchSetupAssistantDefaultScreen()
      .then((decision) => {
        if (!cancelled) setShouldShow(!!decision?.shouldShow);
      })
      .catch(() => {
        if (!cancelled) setShouldShow(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  if (shouldShow === null) {
    return <LoadingScreen />;
  }

  if (shouldShow) {
    return <Redirect to={ROUTES.APP_SETUP_ASSISTANT} />;
  }

  return fallback;
}

SetupAssistantDefaultScreen.propTypes = {
  fallback: PropTypes.node,
};
SetupAssistantDefaultScreen.defaultProps = {
  fallback: null,
};

export default SetupAssistantDefaultScreen;
